import { useEffect, useState, useContext } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { UserContext } from "../../../context/UserContext";
import axios from "axios";
import { BASELINE } from "../../../util";
import ModalComponent from "../pick_modal";
import { AiOutlineInfoCircle } from 'react-icons/ai';

ChartJS.register(ArcElement, Tooltip, Legend);

const OverallSATAnal = ({ setInfoMessage }) => {
  const { user } = useContext(UserContext);

  const [selected, setSelected] = useState('English');
  const [report_eng, set_report_eng] = useState([]);
  const [report_math, set_report_math] = useState([]);
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const [topic, settopic] = useState(false);
  const [section, setsection] = useState("");

  const [esubjects, esetSubjects] = useState([
    { name: 'Inferences', count: 0, max: 199 },
    { name: 'Central Ideas and Details', count: 0, max: 185 },
    { name: 'Command of Evidence', count: 0, max: 312 },
    { name: 'Words in Context', count: 0, max: 290 },
    { name: 'Text Structure and Purpose', count: 0, max: 231 },
    { name: 'Cross-Text Connections', count: 0, max: 157 },
    { name: 'Rhetorical Synthesis', count: 0, max: 252 },
    { name: 'Transitions', count: 0, max: 240 },
    { name: 'Boundaries', count: 0, max: 279 },
    { name: 'Form, Structure, and Sense', count: 0, max: 267 }
  ]);

  const [msubjects, msetSubjects] = useState([
    { name: 'Linear functions', count: 0, max: 87 },
    { name: 'Systems of two linear equations in two variables', count: 0, max: 66 },
    { name: 'Linear inequalities in one or two variables', count: 0, max: 47 },
    { name: 'Linear equations in one variable', count: 0, max: 65 },
    { name: 'Linear equations in two variables', count: 0, max: 78 },
    { name: 'Nonlinear functions', count: 0, max: 132 },
    { name: 'Equivalent expressions', count: 0, max: 79 },
    { name: 'Nonlinear equations in one variable and systems of equations in two variables', count: 0, max: 95 },
    { name: 'Ratios, rates, proportional relationships, and units', count: 0, max: 57 },
    { name: 'Inference from sample statistics and margin of error', count: 0, max: 20 },
    { name: 'Probability and conditional probability', count: 0, max: 36 },
    { name: 'Percentages', count: 0, max: 53 },
    { name: 'One-variable data: Distributions and measures of center and spread', count: 0, max: 56 },
    { name: 'Two-variable data: Models and scatterplots', count: 0, max: 45 },
    { name: 'Evaluating statistical claims: Observational studies and experiments', count: 0, max: 10 },
    { name: 'Lines, angles, and triangles', count: 0, max: 49 },
    { name: 'Right triangles and trigonometry', count: 0, max: 31 },
    { name: 'Area and volume', count: 0, max: 44 },
    { name: 'Circles', count: 0, max: 29 }
  ]);

  useEffect(() => { getting_problems(); }, []);

  const getting_problems = () => {
    axios.get(`${BASELINE}tests/byuser/${user.u_id}`)
      .then(response => {
        const allIncorrectIds = response.data.reduce((acc, test) => {
          if (test.Incorrect_questionIds) acc = acc.concat(JSON.parse(test.Incorrect_questionIds));
          return acc;
        }, []);
        const allcorrectIds = response.data.reduce((acc, test) => {
          if (test.Correct_questionIds) acc = acc.concat(JSON.parse(test.Correct_questionIds));
          return acc;
        }, []);
        const allQuestions = [...allIncorrectIds, ...allcorrectIds];
        const engQuestions = allQuestions.filter(q => q.tag === 'eng');
        const mathQuestions = allQuestions.filter(q => q.tag === 'math');
        const groupedByEngTopic = groupByTopic(engQuestions);
        const groupedByMathTopic = groupByTopic(mathQuestions);
        set_report_eng(transformData(groupedByEngTopic));
        set_report_math(transformData(groupedByMathTopic));
        esetSubjects(prev => updateSubjectsCounts(groupedByEngTopic, prev));
        msetSubjects(prev => updateSubjectsCounts(groupedByMathTopic, prev));
      })
      .catch(error => console.error("Error fetching user tests:", error));
  };

  const groupByTopic = (questions) => {
    const seenIds = new Set();
    return questions.reduce((acc, question) => {
      const uniqueId = question.questionIds || question.id;
      if (seenIds.has(uniqueId)) return acc;
      seenIds.add(uniqueId);
      const topic = question.topic;
      if (!acc[topic]) acc[topic] = { correct: 0, omitted: 0, total: 0 };
      if (question.selectedOption === null) acc[topic].omitted++;
      else if (question.correct) acc[topic].correct++;
      else acc[topic].incorrect++;
      acc[topic].total++;
      return acc;
    }, {});
  };

  const calculatePercentage = (count, total) => `${((count / total) * 100).toFixed(1)}%`;

  const transformData = (groupedData) =>
    Object.entries(groupedData).map(([topic, counts]) => ({
      name: topic,
      correct: calculatePercentage(counts.correct, counts.total),
      incorrect: calculatePercentage(counts.incorrect, counts.total),
      omitted: calculatePercentage(counts.omitted, counts.total),
    }));

  const updateSubjectsCounts = (groupedData, subjectsArray) =>
    subjectsArray.map(subject => {
      const normalizedSubjectName = subject.name.replace(/\s+/g, "").toLowerCase();
      const matchedKey = Object.keys(groupedData).find(
        key => key.replace(/\s+/g, "").toLowerCase() === normalizedSubjectName
      );
      return matchedKey ? { ...subject, count: groupedData[matchedKey].total } : subject;
    });

  const handleRowClick = (subjectName, sec) => {
    setsection(sec);
    settopic(subjectName);
    setIsModalOpen2(true);
  };

  const displayedSubjects = selected === "English" ? esubjects : msubjects;

  const getBarColor = (pct) => {
    if (pct >= 70) return "#10b981";
    if (pct >= 40) return "#3b82f6";
    return "#f87171";
  };

  const getLetterGrade = (pct) => {
    if (pct >= 80) return { grade: 'A', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    if (pct >= 60) return { grade: 'B', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
    if (pct >= 40) return { grade: 'C', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    if (pct >= 20) return { grade: 'D', cls: 'bg-orange-50 text-orange-700 border-orange-200' };
    return { grade: 'F', cls: 'bg-red-50 text-red-700 border-red-200' };
  };

  return (
    <>
      <ModalComponent topic={topic} isOpen={isModalOpen2} onClose={setIsModalOpen2} section={section} type={'SAT'} />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm h-full flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-4 rounded-sm bg-indigo-500" />
            <span className="text-base font-bold text-slate-800">Topic Completion</span>
            <div className="relative group/tip ml-1">
              <AiOutlineInfoCircle className="text-slate-400 hover:text-slate-600 transition-colors cursor-default" size={14} />
              <div className="pointer-events-none absolute bottom-full left-0 mb-2 z-[99999] w-52 opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200">
                <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-xs text-slate-600 leading-relaxed">
                  Shows how many questions you've attempted per topic out of the total available.
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1.5">
            {["English", "Math"].map(tab => (
              <button
                key={tab}
                onClick={() => setSelected(tab)}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all duration-150 ${
                  selected === tab
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Topic rows */}
        <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-slate-50">
          {displayedSubjects.map((item) => {
            const percentage = item.max > 0 ? Number(((item.count / item.max) * 100).toFixed(0)) : 0;
            const { grade, cls } = getLetterGrade(percentage);
            return (
              <div
                key={item.name}
                className="group px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => handleRowClick(item.name, selected)}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors leading-tight truncate pr-2">
                    {item.name}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${cls}`}>
                      {grade}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-500 min-w-[32px] text-right">
                      {percentage}%
                    </span>
                  </div>
                </div>
                <div className="relative w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="absolute h-full rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${percentage}%`, background: getBarColor(percentage) }}
                  />
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {item.count} / {item.max} questions
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default OverallSATAnal;
